import React from 'react';
import {
  Box, Typography, Switch, Tabs, Tab, Button, useMediaQuery,
  TextField, InputAdornment, CircularProgress,
} from '@mui/material';
import {
  DoneAll as DoneAllIcon,
  DeleteSweep as DeleteSweepIcon,
} from '@mui/icons-material';
import { BRAND, FONT_STACK, GRADIENT_GREEN, brandButtonSx, brandGhostButtonSx, brandFieldSx } from '../../brand';

// ============================================================================
//  adminUi — shared chrome for every Admin Panel screen
// ============================================================================
//
//  The Admin Panel grew one screen at a time, and it shows: twenty-one forms,
//  each with its own header treatment, its own idea of green, its own spacing.
//  On a phone several of them are unusable — fields sized for a 1400px desktop,
//  tab strips that overflow with no way to scroll, tables that push the page
//  sideways.
//
//  This module is the fix, and it is deliberately a KIT rather than a rewrite.
//  Converting nineteen thousand lines of admin forms in one pass would be a
//  reckless way to touch screens that configure drug catalogues and user
//  permissions. Instead: the primitives live here, screens adopt them one at a
//  time, and every screen converted lands on the same navy/green system as
//  Setup and Login — because all of it reads from brand.js.
//
//  Everything here is responsive by construction. There is no "mobile version"
//  to keep in sync — a component either works at 360px or it is not finished.
//
//  A note on layout: this file uses CSS Grid, never MUI's <Grid>. In MUI 7 the
//  old `<Grid item xs={12} md={6}>` API is silently IGNORED — no error, no
//  warning, the props simply do nothing and every field renders full-width.
//  Several forms in this folder are already carrying that bug. CSS Grid has no
//  such trapdoor.

// ── Tokens ──────────────────────────────────────────────────────────────────

export const ADMIN = {
  sidebarWidth: 288,

  // Width of the per-screen section nav (AdminSideNav) — the second, inner
  // column, not the Admin Panel's own sidebar. Wide enough for a two-word
  // label, a count badge and a one-line caption without wrapping.
  navWidth: 244,

  // The Admin Panel renders inside MainLayout's <Main>, which sits below a
  // FIXED AppBar. Anything sticky in here has to be offset by the height of
  // that bar or it slides underneath it. MUI's toolbar is 56px on xs, 64px
  // from sm up — the same values MainLayout's <ToolbarSpacer> resolves to.
  appBarHeight: { xs: 56, sm: 64 },

  // Where a sticky element inside the panel should come to rest: clear of the
  // fixed AppBar, with a little air. Used by the sidebar rail and by the
  // per-user headers in the Activity Log.
  //
  // Being a few pixels out here is cosmetic — the element sits slightly high
  // or low. That is deliberately a much softer failure than sizing a
  // full-height panel off the same numbers, where being a few pixels out
  // gives you a second scrollbar and a strip of dead space at the bottom.
  stickyTop: { xs: 68, sm: 76 },

  radius:      '14px',
  radiusSm:    '10px',
  shadow:      '0 4px 20px rgba(15,31,61,.06)',
  shadowLift:  '0 10px 32px rgba(15,31,61,.10)',

  // ── Vertical rhythm ───────────────────────────────────────────────────────
  //
  //  Two values, used by the shell and by every screen header, so the top of
  //  every admin screen breathes the same amount.
  //
  //  These exist because the panel had NO top spacing at all. When the content
  //  area stopped being its own scroll container it lost the padding that came
  //  with it, so the first thing on every screen — a navy header band on the
  //  converted ones, a bare heading on the rest — sat flush against the top
  //  edge, and the first card sat straight under the header with 20px between
  //  them. Everything read as one stack of touching rectangles.
  //
  //  topGap  — above the page header, separating it from the panel edge.
  //  headGap — below any page header, before the first card of the screen.
  topGap:  { xs: 1,   md: 1.5 },
  headGap: { xs: 2.5, sm: 3.5 },
};

export { BRAND, FONT_STACK, GRADIENT_GREEN, brandButtonSx, brandGhostButtonSx, brandFieldSx };

// Applied to every TextField/Select on a converted admin screen so focus rings
// and borders match the rest of the product.
export const adminFieldSx = brandFieldSx;

// ── Page header ─────────────────────────────────────────────────────────────
//
//  The band at the top of a screen. Replaces the per-screen <AppBar> several
//  admin forms carry — an AppBar nested inside a page that already has one is
//  a second, competing header, and on a phone it costs a fifth of the screen.

export function AdminPageHead({ icon: Icon, title, subtitle, actions }) {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', sm: 'center' },
        flexDirection: { xs: 'column', sm: 'row' },
        gap: { xs: 1.5, sm: 2 },
        px: { xs: 2, sm: 2.5 },
        py: { xs: 1.75, sm: 2 },
        // Shared with the shell's plain heading, so a converted screen and an
        // unconverted one leave the same gap before their first card.
        mb: ADMIN.headGap,
        borderRadius: ADMIN.radius,
        background: `linear-gradient(135deg, ${BRAND.navy} 0%, ${BRAND.navyDark} 100%)`,
        color: '#fff',
        boxShadow: `0 8px 24px ${BRAND.navy}26`,
      }}
    >
      {Icon && (
        <Box
          sx={{
            width: 40, height: 40, borderRadius: ADMIN.radiusSm, flexShrink: 0,
            background: 'rgba(255,255,255,.14)',
            display: 'grid', placeItems: 'center',
          }}
        >
          <Icon sx={{ fontSize: 21 }} />
        </Box>
      )}

      <Box sx={{ minWidth: 0, flex: 1 }}>
        <Typography sx={{ fontSize: { xs: 16, sm: 18 }, fontWeight: 800, lineHeight: 1.3 }}>
          {title}
        </Typography>
        {subtitle && (
          <Typography sx={{ fontSize: { xs: 12, sm: 12.5 }, opacity: .82, mt: .25, lineHeight: 1.5 }}>
            {subtitle}
          </Typography>
        )}
      </Box>

      {actions && (
        <Box sx={{ display: 'flex', gap: 1, width: { xs: '100%', sm: 'auto' }, flexShrink: 0 }}>
          {actions}
        </Box>
      )}
    </Box>
  );
}

// ── Card ────────────────────────────────────────────────────────────────────

export function AdminCard({ icon: Icon, title, subtitle, action, children, footer, sx }) {
  return (
    <Box
      sx={{
        background: BRAND.surface,
        border: `1px solid ${BRAND.line}`,
        borderRadius: ADMIN.radius,
        boxShadow: ADMIN.shadow,
        overflow: 'hidden',
        ...sx,
      }}
    >
      {(title || action) && (
        <Box
          sx={{
            display: 'flex', alignItems: 'flex-start', gap: 1.75,
            px: { xs: 2, sm: 3 },
            pt: { xs: 2.25, sm: 2.75 },
            pb: { xs: 1.75, sm: 2 },
            borderBottom: `1px solid ${BRAND.line}`,
          }}
        >
          {Icon && (
            <Box
              sx={{
                width: 38, height: 38, borderRadius: ADMIN.radiusSm, flexShrink: 0,
                background: BRAND.navySoft, display: 'grid', placeItems: 'center',
              }}
            >
              <Icon sx={{ fontSize: 20, color: BRAND.navy }} />
            </Box>
          )}

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography sx={{ fontSize: { xs: 15.5, sm: 17 }, fontWeight: 800, color: BRAND.ink, lineHeight: 1.3 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography sx={{ fontSize: 12.5, color: BRAND.inkMuted, mt: .35, lineHeight: 1.55 }}>
                {subtitle}
              </Typography>
            )}
          </Box>

          {action && <Box sx={{ flexShrink: 0, ml: 'auto' }}>{action}</Box>}
        </Box>
      )}

      <Box sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2.25, sm: 2.75 } }}>{children}</Box>

      {footer && (
        <Box
          sx={{
            px: { xs: 2, sm: 3 }, py: { xs: 1.75, sm: 2 },
            borderTop: `1px solid ${BRAND.line}`,
            background: BRAND.canvas,
          }}
        >
          {footer}
        </Box>
      )}
    </Box>
  );
}

// ── Form layout ─────────────────────────────────────────────────────────────
//
//  A deterministic 1 → 2 → 3 column progression rather than CSS `auto-fit`.
//  auto-fit is tempting, but on a 1600px monitor it will happily lay a form
//  out five columns wide, and a five-column form does not read as a form — it
//  reads as a spreadsheet. Fixed breakpoints keep the shape predictable.
//
//  minmax(0, 1fr) rather than a bare 1fr: grid columns default to min-content,
//  so one long unbroken string (a URL, a base64 logo) would otherwise stretch
//  the column and push the page sideways.

export function FormGrid({ children, columns = { sm: 2, lg: 3 }, sx }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gap: { xs: 2, sm: 2.25 },
        alignItems: 'start',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          sm: `repeat(${columns.sm}, minmax(0, 1fr))`,
          lg: `repeat(${columns.lg || columns.sm}, minmax(0, 1fr))`,
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// Makes a child span the whole row at every size — for a textarea, a wide
// select, or anything that would look stranded in a single column.
export function FormSpan({ children, sx }) {
  return <Box sx={{ gridColumn: '1 / -1', minWidth: 0, ...sx }}>{children}</Box>;
}

// ── Card grid ───────────────────────────────────────────────────────────────
//
//  Two panels per row from `md` up — the six-and-six split. A settings screen
//  laid out as one full-width column down the page wastes the right half of
//  every monitor it is opened on and turns a dozen sections into a long scroll
//  for no reason.
//
//  `alignItems: start` matters: without it, grid stretches both cells to the
//  height of the taller one, so a two-line panel beside an expanded one grows
//  a tall empty box. With it, each panel is only as tall as its content and
//  the shorter one simply leaves whitespace beneath it.
//
//  Stays ONE column below md. Two columns on a phone would give each panel
//  about 160px — narrower than the fields inside them.

export function CardGrid({ children, columns = 2, sx }) {
  return (
    <Box
      sx={{
        display: 'grid',
        alignItems: 'start',
        gap: { xs: 1.75, sm: 2, lg: 2.5 },
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          md: `repeat(${columns}, minmax(0, 1fr))`,
        },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// A panel that takes the full row inside a CardGrid — for something with a
// wide table, or a section that reads badly at half width.
export function CardSpan({ children, sx }) {
  return <Box sx={{ gridColumn: { md: '1 / -1' }, minWidth: 0, ...sx }}>{children}</Box>;
}

// ── Toggle ──────────────────────────────────────────────────────────────────
//
//  A bare <Switch> with a label floating in a form column reads as an
//  afterthought — there is nothing to say where the control starts or ends,
//  and on a phone the whole row becomes a 20px tap target next to some text.
//  Wrapping it in a tile gives it a boundary, makes the ENTIRE tile tappable,
//  and leaves room for the one line of explanation these settings usually
//  need and never have.

export function ToggleField({ name, checked, onChange, label, description, disabled }) {
  const toggle = () => {
    if (disabled) return;
    // Shaped like a real change event so existing handleInputChange functions
    // — which read e.target.name / .type / .checked — work unmodified. That is
    // what lets a screen adopt this without touching its state logic.
    onChange({ target: { name, type: 'checkbox', checked: !checked, value: !checked } });
  };

  return (
    <Box
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      tabIndex={disabled ? -1 : 0}
      onClick={toggle}
      onKeyDown={(e) => {
        if (e.key === ' ' || e.key === 'Enter') { e.preventDefault(); toggle(); }
      }}
      sx={{
        display: 'flex', alignItems: 'center', gap: 1.5,
        px: 1.75, py: 1.5,
        minHeight: 56,
        borderRadius: ADMIN.radiusSm,
        border: `1px solid ${checked ? BRAND.green : BRAND.line}`,
        background: checked ? BRAND.greenSoft : BRAND.surface,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? .55 : 1,
        transition: 'border-color .18s ease, background-color .18s ease',
        '&:hover': disabled ? {} : { borderColor: checked ? BRAND.greenDark : BRAND.navyLight },
        '&:focus-visible': { outline: `2px solid ${BRAND.navy}`, outlineOffset: 2 },
      }}
    >
      <Switch
        name={name}
        checked={!!checked}
        disabled={disabled}
        // The tile owns the click; the switch is the indicator. Without this
        // the click fires twice and the toggle appears not to work at all.
        onChange={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
        tabIndex={-1}
        size="small"
        color="success"
        sx={{ flexShrink: 0, pointerEvents: 'none' }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 13.5, fontWeight: 600, color: BRAND.ink, lineHeight: 1.35 }}>
          {label}
        </Typography>
        {description && (
          <Typography sx={{ fontSize: 11.5, color: BRAND.inkMuted, mt: .25, lineHeight: 1.45 }}>
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── Form actions ────────────────────────────────────────────────────────────
//
//  Right-aligned from tablet up. On a phone they stack full-width and REVERSE,
//  so the primary action sits at the bottom — nearest the thumb, and last in
//  reading order, which is where a confirm belongs.

export function FormActions({ children, sx }) {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column-reverse', sm: 'row' },
        justifyContent: 'flex-end',
        gap: 1.25,
        mt: { xs: 2.5, sm: 3 },
        pt: { xs: 2, sm: 2.5 },
        borderTop: `1px solid ${BRAND.line}`,
        '& > *': { width: { xs: '100%', sm: 'auto' } },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

export function PrimaryButton({ children, sx, ...rest }) {
  return (
    <Button variant="contained" sx={{ ...brandButtonSx, py: 1.05, px: 2.5, fontSize: 13.5, ...sx }} {...rest}>
      {children}
    </Button>
  );
}

export function GhostButton({ children, sx, ...rest }) {
  return (
    <Button variant="outlined" sx={{ ...brandGhostButtonSx, py: 1.05, px: 2.5, fontSize: 13.5, ...sx }} {...rest}>
      {children}
    </Button>
  );
}

// ── Tabs ────────────────────────────────────────────────────────────────────
//
//  Always scrollable, always with scroll buttons on mobile. The default MUI
//  tab strip silently CLIPS tabs that do not fit — on a 360px screen a
//  four-tab strip loses its last two tabs and there is no indication they
//  exist, which is how a screen ends up with features nobody can reach.
//
//  tabs: [{ icon, label, shortLabel? }]

export function AdminTabs({ value, onChange, tabs, sx }) {
  const isMobile = useMediaQuery('(max-width:600px)');

  return (
    <Box sx={{ borderBottom: `1px solid ${BRAND.line}`, mb: { xs: 2, sm: 2.5 }, ...sx }}>
      <Tabs
        value={value}
        onChange={onChange}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{
          minHeight: 0,
          '& .MuiTabs-indicator': { height: 3, borderRadius: '3px 3px 0 0', background: GRADIENT_GREEN },
          '& .MuiTab-root': {
            textTransform: 'none',
            fontWeight: 600,
            fontSize: { xs: 12.5, sm: 13.5 },
            minHeight: { xs: 48, sm: 52 },
            minWidth: 'auto',
            px: { xs: 1.5, sm: 2.25 },
            color: BRAND.inkMuted,
            '&.Mui-selected': { color: BRAND.navy, fontWeight: 700 },
          },
          '& .MuiTab-root .MuiSvgIcon-root': { fontSize: 18 },
        }}
      >
        {tabs.map((tab, i) => (
          <Tab
            key={i}
            icon={tab.icon}
            iconPosition={isMobile ? 'top' : 'start'}
            label={isMobile ? (tab.shortLabel || tab.label) : tab.label}
          />
        ))}
      </Tabs>
    </Box>
  );
}

// ── Vertical section nav ────────────────────────────────────────────────────
//
//  A screen with three or more sections reads better as a list down the side
//  than as a horizontal tab strip. The strip forces every label to compete for
//  one line of width — which is why several admin screens abbreviate to
//  "Add Co." — and it gives no room to say what a section is for or how much
//  is in it.
//
//  Down the side there is room for all three: the name, a one-line caption,
//  and a live count. The result is closer to navigation than to tabs, which is
//  what these sections actually are.
//
//  Below `md` it collapses to a horizontal scrolling chip row. Not a tab strip
//  — chips wrap their own width, so a long label shortens the row rather than
//  squeezing its neighbours.
//
//  items: [{ icon, label, shortLabel?, caption?, badge? }]

export function AdminSideNav({ value, onChange, items, stats, sx }) {
  const pick = (i) => (e) => onChange?.(e, i);

  return (
    <Box sx={sx}>
      {/* ── md and up: the list ── */}
      <Box
        sx={{
          display: { xs: 'none', md: 'flex' }, flexDirection: 'column', gap: .5,
          position: 'sticky', top: ADMIN.stickyTop,
        }}
      >
        {items.map((item, i) => {
          const on = value === i;
          const Icon = item.icon;
          return (
            <Box
              key={i}
              component="button" type="button" onClick={pick(i)}
              aria-current={on ? 'page' : undefined}
              sx={{
                display: 'flex', alignItems: 'flex-start', gap: 1.5, width: '100%',
                px: 1.5, py: 1.375, borderRadius: ADMIN.radiusSm,
                cursor: 'pointer', font: 'inherit', textAlign: 'left',
                background: on ? '#fff' : 'transparent',
                border: '1px solid', borderColor: on ? BRAND.line : 'transparent',
                boxShadow: on ? ADMIN.shadow : 'none',
                transition: 'background .15s ease, border-color .15s ease',
                '&:hover': { background: on ? '#fff' : 'rgba(15,31,61,.04)' },
                '&:focus-visible': { outline: `2px solid ${BRAND.green}`, outlineOffset: 2 },
              }}
            >
              {/* active marker */}
              <Box
                sx={{
                  width: 3, alignSelf: 'stretch', borderRadius: 2, flexShrink: 0,
                  background: on ? BRAND.green : 'transparent',
                }}
              />

              {Icon && (
                <Box
                  sx={{
                    width: 30, height: 30, borderRadius: '8px', flexShrink: 0, mt: .125,
                    display: 'grid', placeItems: 'center',
                    background: on ? `${BRAND.green}17` : 'rgba(15,31,61,.05)',
                  }}
                >
                  <Icon sx={{ fontSize: 17, color: on ? BRAND.green : BRAND.inkFaint }} />
                </Box>
              )}

              <Box sx={{ minWidth: 0, flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography
                    sx={{
                      flex: 1, minWidth: 0, fontSize: 13,
                      fontWeight: on ? 800 : 600,
                      color: on ? BRAND.navy : BRAND.inkMuted,
                    }}
                    noWrap
                  >
                    {item.label}
                  </Typography>
                  {item.badge !== undefined && item.badge !== null && (
                    <Typography
                      sx={{
                        flexShrink: 0, fontSize: 10.5, fontWeight: 800,
                        px: .875, py: .125, borderRadius: '20px',
                        background: on ? BRAND.green : 'rgba(15,31,61,.07)',
                        color: on ? '#fff' : BRAND.inkFaint,
                      }}
                    >
                      {item.badge}
                    </Typography>
                  )}
                </Box>
                {item.caption && (
                  <Typography sx={{ fontSize: 11, color: BRAND.inkFaint, lineHeight: 1.45, mt: .25 }}>
                    {item.caption}
                  </Typography>
                )}
              </Box>
            </Box>
          );
        })}

        {/* ── at-a-glance figures ── */}
        {stats?.length > 0 && (
          <Box
            sx={{
              mt: 2, pt: 2, borderTop: `1px solid ${BRAND.line}`,
              display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 1,
            }}
          >
            {stats.map((s) => (
              <Box
                key={s.label}
                sx={{
                  px: 1.25, py: 1, borderRadius: ADMIN.radiusSm, minWidth: 0,
                  background: '#fff', border: `1px solid ${BRAND.line}`,
                }}
              >
                <Typography
                  sx={{ fontSize: 17, fontWeight: 800, lineHeight: 1.2, color: s.accent || BRAND.navy }}
                  noWrap
                >
                  {s.value}
                </Typography>
                <Typography
                  sx={{ fontSize: 9.5, fontWeight: 700, color: BRAND.inkFaint, letterSpacing: '.4px', textTransform: 'uppercase', lineHeight: 1.35 }}
                >
                  {s.label}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* ── below md: chip row ── */}
      <Box
        sx={{
          display: { xs: 'flex', md: 'none' }, gap: 1,
          overflowX: 'auto', pb: 1, mb: 1.5,
          '&::-webkit-scrollbar': { display: 'none' }, scrollbarWidth: 'none',
        }}
      >
        {items.map((item, i) => {
          const on = value === i;
          const Icon = item.icon;
          return (
            <Box
              key={i}
              component="button" type="button" onClick={pick(i)}
              sx={{
                display: 'flex', alignItems: 'center', gap: .75, flexShrink: 0,
                px: 1.5, py: .875, borderRadius: '20px',
                cursor: 'pointer', font: 'inherit',
                background: on ? BRAND.navy : '#fff',
                border: `1px solid ${on ? BRAND.navy : BRAND.line}`,
                color: on ? '#fff' : BRAND.inkMuted,
              }}
            >
              {Icon && <Icon sx={{ fontSize: 15 }} />}
              <Typography sx={{ fontSize: 12, fontWeight: 700, whiteSpace: 'nowrap' }}>
                {item.shortLabel || item.label}
                {item.badge !== undefined && item.badge !== null ? ` · ${item.badge}` : ''}
              </Typography>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
}

// ── Workspace ───────────────────────────────────────────────────────────────
//
//  Side nav beside the working area. The content column is `minmax(0, 1fr)`,
//  not `1fr`: a grid child defaults to min-width:auto, so one wide table would
//  otherwise push the whole column past the viewport instead of scrolling
//  inside its own TableWrap.

// ── Fill the viewport instead of hanging in the middle of it ────────────────
//
//  useFitRows(...) -> how many table rows actually FIT below the card, right now.
//
//  THE PROBLEM. Every table in this panel opens at `rowsPerPage = 5`. On a
//  laptop that draws a card about a third of the window tall with a wide band of
//  empty page under it, and a pager offering "1–5 of 82". The information is
//  there; you just have to click through seventeen pages to read it, on a screen
//  with room for four times as much at once.
//
//  Five is not a considered default — it is MUI's example value. This replaces it
//  with the only number that is actually right: however many fit.
//
//  MEASURED, NOT CALCULATED. The obvious version subtracts a stack of constants —
//  app bar, header, search box, thead, pager — and is wrong the first time any of
//  them changes, in a way that shows up as a stray scrollbar or a clipped last
//  row. This reads the real geometry instead:
//
//    · the table container's own top, from getBoundingClientRect
//    · the rendered heights of thead and the pager
//    · the height of a REAL row, because the two tables on one screen differ —
//      a ward row carries a progress bar and runs ~59px, a bed row ~47px
//
//  so it needs no knowledge of the page above it and cannot drift from it.
//
//  IT STOPS THE MOMENT THE USER DISAGREES. `enabled` is passed false once someone
//  picks a size from the pager. An auto-fit that overrides a deliberate choice on
//  the next resize is worse than no auto-fit at all.
export function useFitRows({
  containerRef,
  pagerRef,
  //  Re-measure when these change — the data arriving is what gives the table a
  //  real row to measure in the first place.
  deps = [],
  enabled = true,
  min = 5,
  //  A ceiling, not a target. Without it a short row on a tall screen asks for
  //  hundreds of rows at once, and the pager stops being a pager.
  max = 60,
  //  Only used before a real row exists to measure.
  estimateRowHeight = 48,
  //  Air below the card, so the last row is not flush against the window edge.
  gutter = 28,
}) {
  const [rows, setRows] = React.useState(min);

  React.useLayoutEffect(() => {
    if (!enabled) return undefined;

    const measure = () => {
      const el = containerRef.current;
      if (!el || typeof window === 'undefined') return;

      const top = el.getBoundingClientRect().top;
      //  Nothing sensible to say while the card is off-screen or not laid out.
      if (top <= 0) return;

      const head = el.querySelector('thead');
      const firstRow = el.querySelector('tbody tr');
      const headH = head ? head.offsetHeight : 0;
      const rowH = firstRow && firstRow.offsetHeight > 0
        ? firstRow.offsetHeight
        : estimateRowHeight;
      const pagerH = pagerRef && pagerRef.current ? pagerRef.current.offsetHeight : 0;

      const room = window.innerHeight - top - headH - pagerH - gutter;
      const fit = Math.floor(room / rowH);
      setRows(Math.min(max, Math.max(min, fit)));
    };

    measure();

    //  A ResizeObserver on the container as well as the window listener: the
    //  Admin Panel's own sidebar collapses, and a card that gets wider can get
    //  shorter as rows stop wrapping. Guarded because jsdom in the older test
    //  harness has no ResizeObserver.
    let ro = null;
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      ro = new ResizeObserver(() => measure());
      ro.observe(containerRef.current);
    }
    window.addEventListener('resize', measure);
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, min, max, gutter, estimateRowHeight, ...deps]);

  return rows;
}

export function AdminWorkspace({ nav, children, sx }) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', md: `${ADMIN.navWidth}px minmax(0, 1fr)` },
        gap: { xs: 0, md: 3 },
        alignItems: 'start',
        ...sx,
      }}
    >
      {nav}
      <Box sx={{ minWidth: 0 }}>{children}</Box>
    </Box>
  );
}

// ── Bulk edit bar ───────────────────────────────────────────────────────────
//
//  The strip above the scheme-records table on Services / Diagnosis / Procedure
//  Setup. You tick the schemes you want, type the values once, and press
//  "Apply to N".
//
//  It exists because those three screens are the same job repeated: one clinical
//  item, the same tariff and code, entered again for every insurance scheme.
//  With five schemes that was five rows × four fields typed by hand, and the
//  only bulk tool was a dialog that copied ONE existing row onto other schemes
//  — which cannot help you when every row is still empty.
//
//  Rules the shape encodes:
//
//    · A BLANK field is skipped, not written. You can push a new tariff to six
//      schemes without disturbing their codes. This is why the bar cannot be a
//      plain "set every column" form.
//    · Both buttons are disabled with nothing ticked, and both name the count,
//      so "Delete 6" is never a surprise.
//    · Delete ignores the fields entirely — it removes the ticked rows.
//
//  fields: [{ key, label, placeholder?, type?, adornment?, width? }]

export function BulkEditBar({
  fields,
  values,
  onChange,
  selectedCount,
  totalCount,
  onApply,
  onDelete,
  applying,
  deleting,
  itemLabel = 'row',
}) {
  const none = selectedCount === 0;
  const busy = !!(applying || deleting);
  // A field the user has actually filled in. Anything left blank is left alone
  // on save, so this is also what decides whether Apply can do anything.
  const filled = fields.filter(f => String(values?.[f.key] ?? '').trim() !== '');

  return (
    <Box
      sx={{
        px: { xs: 1.5, sm: 2 }, py: 1.5,
        borderBottom: `1px solid ${BRAND.line}`,
        background: none ? BRAND.canvas : BRAND.navySoft,
        transition: 'background-color .18s ease',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
        <Typography sx={{ fontSize: 10.5, fontWeight: 800, letterSpacing: '1px', textTransform: 'uppercase', color: BRAND.inkFaint }}>
          Apply to many
        </Typography>
        <Typography sx={{ fontSize: 11.5, fontWeight: 700, color: none ? BRAND.inkMuted : BRAND.navy }}>
          {none
            ? `Tick the ${itemLabel}s you want to change`
            : `${selectedCount} of ${totalCount} ${itemLabel}${selectedCount === 1 ? '' : 's'} ticked`}
        </Typography>
      </Box>

      <Box sx={{
        display: 'grid',
        gap: 1.25,
        alignItems: 'start',
        gridTemplateColumns: {
          xs: 'minmax(0, 1fr)',
          sm: 'repeat(2, minmax(0, 1fr))',
          lg: `repeat(${Math.min(fields.length, 4)}, minmax(0, 1fr))`,
        },
      }}>
        {fields.map((f) => (
          <TextField
            key={f.key}
            size="small"
            label={f.label}
            type={f.type || 'text'}
            placeholder={f.placeholder || 'Leave blank to keep'}
            value={values?.[f.key] ?? ''}
            onChange={(e) => onChange(f.key, e.target.value)}
            disabled={busy}
            InputLabelProps={{ shrink: true }}
            InputProps={f.adornment ? {
              startAdornment: (
                <InputAdornment position="start">
                  <Box component="span" sx={{ fontSize: 12, color: BRAND.inkFaint, fontWeight: 600 }}>{f.adornment}</Box>
                </InputAdornment>
              ),
            } : undefined}
            inputProps={f.type === 'number' ? { min: 0, step: '0.01' } : undefined}
            sx={{ ...brandFieldSx, '& .MuiOutlinedInput-root': { ...brandFieldSx['& .MuiOutlinedInput-root'], background: '#fff' } }}
          />
        ))}
      </Box>

      <Box sx={{
        display: 'flex', alignItems: 'center', gap: 1.25, mt: 1.5, flexWrap: 'wrap',
      }}>
        <Button
          variant="contained"
          size="small"
          onClick={onApply}
          disabled={none || filled.length === 0 || busy}
          startIcon={applying ? <CircularProgress size={13} sx={{ color: '#fff' }} /> : <DoneAllIcon sx={{ fontSize: 16 }} />}
          sx={{ ...brandButtonSx, py: .7, px: 2, fontSize: 12.5 }}
        >
          {applying ? 'Applying…' : none ? 'Apply to all' : `Apply to ${selectedCount}`}
        </Button>

        <Button
          variant="outlined"
          size="small"
          onClick={onDelete}
          disabled={none || busy}
          startIcon={deleting ? <CircularProgress size={13} sx={{ color: BRAND.danger }} /> : <DeleteSweepIcon sx={{ fontSize: 16 }} />}
          sx={{
            borderRadius: '10px', textTransform: 'none', fontWeight: 700, fontSize: 12.5, py: .7, px: 2,
            color: BRAND.danger, borderColor: BRAND.line,
            '&:hover': { borderColor: BRAND.danger, background: '#FDECEC' },
          }}
        >
          {deleting ? 'Deleting…' : none ? 'Delete all' : `Delete ${selectedCount}`}
        </Button>

        {/* Says what Apply will actually touch. Without it, "apply to 6" reads
            as "overwrite all six columns on six rows", which is not what a bar
            that skips blanks does. */}
        {!none && (
          <Typography sx={{ fontSize: 11, color: BRAND.inkMuted, lineHeight: 1.5 }}>
            {filled.length === 0
              ? 'Fill at least one field above'
              : `Will set ${filled.map(f => f.label.toLowerCase()).join(', ')} — blanks are left as they are`}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ── Table wrapper ───────────────────────────────────────────────────────────
//
//  A wide table must scroll INSIDE its own box. Letting it scroll the page is
//  the single most common reason an admin screen is unusable on a phone: the
//  whole layout shifts sideways, the sidebar and header go with it, and there
//  is no way back except scrolling the page left again.

export function TableWrap({ children, sx }) {
  return (
    <Box
      sx={{
        width: '100%',
        overflowX: 'auto',
        WebkitOverflowScrolling: 'touch',
        borderRadius: ADMIN.radiusSm,
        border: `1px solid ${BRAND.line}`,
        '& table': { minWidth: 640 },
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}

// ── Empty state ─────────────────────────────────────────────────────────────

export function EmptyState({ icon: Icon, title, body, action }) {
  return (
    <Box sx={{ textAlign: 'center', py: { xs: 5, sm: 7 }, px: 2 }}>
      {Icon && (
        <Box
          sx={{
            width: 60, height: 60, borderRadius: '50%', mx: 'auto', mb: 2,
            background: BRAND.navySoft, display: 'grid', placeItems: 'center',
          }}
        >
          <Icon sx={{ fontSize: 28, color: BRAND.navy }} />
        </Box>
      )}
      <Typography sx={{ fontSize: 16, fontWeight: 700, color: BRAND.ink, mb: .75 }}>
        {title}
      </Typography>
      {body && (
        <Typography sx={{ fontSize: 13, color: BRAND.inkMuted, maxWidth: 380, mx: 'auto', lineHeight: 1.7 }}>
          {body}
        </Typography>
      )}
      {action && <Box sx={{ mt: 2.5 }}>{action}</Box>}
    </Box>
  );
}

// ── Section label ───────────────────────────────────────────────────────────

export function SectionLabel({ children, sx }) {
  return (
    <Typography
      sx={{
        fontSize: 11, fontWeight: 800, letterSpacing: '1px',
        textTransform: 'uppercase', color: BRAND.inkFaint,
        ...sx,
      }}
    >
      {children}
    </Typography>
  );
}
